import React, { useState, useEffect } from 'react';
import { Document, DocumentAction, Matter, Project, Goal, Task, db } from '../../lib/db';
import { toast } from 'sonner';
import { 
  Calendar, 
  Target, 
  CheckCircle, 
  Plus, 
  CheckSquare,
  Square,
  FileText,
  Loader
} from 'lucide-react';
import { ProcessingModal } from './ProcessingModal';
import { TemplateModal } from '../templates/TemplateModal';
import { AddTaskModal } from './AddTaskModal';
import { CompleteTaskModal } from './CompleteTaskModal';

interface ProcessingPanelProps {
  document: Document;
  dbUserId: string;
  documentActions: DocumentAction[];
  onUpdate: () => void;
}

interface ProcessingOption {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  color: string;
}

export function ProcessingPanel({ document, dbUserId, documentActions, onUpdate }: ProcessingPanelProps) {
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());
  const [processing, setProcessing] = useState(false);
  const [notes, setNotes] = useState('');
  
  // Modal states
  const [showDeadlineModal, setShowDeadlineModal] = useState(false);
  const [showDeliverableModal, setShowDeliverableModal] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showCompleteTaskModal, setShowCompleteTaskModal] = useState(false);
  
  // Data for modals
  const [matters, setMatters] = useState<Matter[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [deliverables, setDeliverables] = useState<Goal[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);

  const processingOptions: ProcessingOption[] = [
    {
      id: 'deadline',
      label: 'Create Deadline',
      icon: <Calendar className="w-5 h-5" />,
      description: 'Add a deadline to a matter',
      color: 'text-red-600'
    },
    {
      id: 'deliverable',
      label: 'Create Deliverable',
      icon: <Target className="w-5 h-5" />,
      description: 'Create a new deliverable from template',
      color: 'text-green-600'
    },
    {
      id: 'add-task',
      label: 'Add Task to Deliverable',
      icon: <Plus className="w-5 h-5" />,
      description: 'Add a new task to an existing deliverable',
      color: 'text-blue-600'
    },
    {
      id: 'complete-task',
      label: 'Mark Task Complete',
      icon: <CheckCircle className="w-5 h-5" />,
      description: 'Mark an existing task as completed',
      color: 'text-purple-600'
    }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [mattersList, projectsList, deliverablesList] = await Promise.all([
        db.matters.findByUserId(dbUserId),
        db.projects.findByUserId(dbUserId),
        db.goals.findByUserId(dbUserId)
      ]);
      setMatters(mattersList);
      setProjects(projectsList);
      setDeliverables(deliverablesList.filter(g => !g.is_archived));
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const toggleOption = (optionId: string) => {
    const newSelected = new Set(selectedOptions);
    if (newSelected.has(optionId)) {
      newSelected.delete(optionId);
    } else {
      newSelected.add(optionId);
    }
    setSelectedOptions(newSelected);
  };

  const handleProcess = async () => {
    if (selectedOptions.size === 0) {
      toast.error('Please select at least one action');
      return;
    }

    setProcessing(true);
    
    try {
      // Process selected options in order
      for (const optionId of selectedOptions) {
        switch (optionId) {
          case 'deadline':
            setShowDeadlineModal(true);
            break;
          case 'deliverable':
            setShowDeliverableModal(true);
            break;
          case 'add-task':
            setShowAddTaskModal(true);
            break;
          case 'complete-task':
            setShowCompleteTaskModal(true);
            break;
        }
      }
    } catch (error) {
      console.error('Error during processing:', error);
      toast.error('An error occurred during processing');
    } finally {
      setProcessing(false);
    }
  };

  const handleCompleteProcessing = async () => {
    try {
      // Update document status to processed
      await db.documents.update(document.id, dbUserId, {
        status: 'processed',
        processed_at: new Date(),
        processing_notes: notes
      });
      
      toast.success('Document processing completed');
      onUpdate();
    } catch (error) {
      console.error('Error completing processing:', error);
      toast.error('Failed to complete processing');
    }
  };

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'create_deadline':
        return <Calendar className="w-4 h-4 text-red-600" />;
      case 'create_deliverable':
        return <Target className="w-4 h-4 text-green-600" />;
      case 'add_task':
        return <Plus className="w-4 h-4 text-blue-600" />;
      case 'complete_task':
        return <CheckCircle className="w-4 h-4 text-purple-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getActionLabel = (actionType: string) => {
    switch (actionType) {
      case 'create_deadline':
        return 'Created deadline';
      case 'create_deliverable':
        return 'Created deliverable';
      case 'add_task':
        return 'Added task';
      case 'complete_task':
        return 'Completed task';
      default:
        return actionType;
    }
  };

  return (
    <>
      <div className="h-full flex flex-col">
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Document Details</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="font-medium text-gray-700">Filename:</span>
              <p className="text-gray-600">{document.suggested_filename || document.filename}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Status:</span>
              <p className="text-gray-600 capitalize">{document.status}</p>
            </div>
            {document.processing_notes && (
              <div>
                <span className="font-medium text-gray-700">Notes:</span>
                <p className="text-gray-600">{document.processing_notes}</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Actions Taken */}
        {documentActions.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Actions Taken</h4>
            <div className="space-y-2">
              {documentActions.map((action) => (
                <div key={action.id} className="flex items-center space-x-2 text-sm">
                  {getActionIcon(action.action_type)}
                  <span className="text-gray-600">{getActionLabel(action.action_type)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Processing Options */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            What actions does this document trigger?
          </h4>
          <div className="space-y-3">
            {processingOptions.map((option) => (
              <div
                key={option.id}
                onClick={() => toggleOption(option.id)}
                className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="mt-0.5">
                  {selectedOptions.has(option.id) ? (
                    <CheckSquare className="w-5 h-5 text-indigo-600" />
                  ) : (
                    <Square className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className={option.color}>{option.icon}</span>
                    <span className="font-medium text-gray-900">{option.label}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Processing Notes */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Processing Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Add any notes about processing this document..."
          />
        </div>
        
        {/* Action Buttons */}
        <div className="mt-auto space-y-3">
          {selectedOptions.size > 0 && (
            <button
              onClick={handleProcess}
              disabled={processing}
              className="w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {processing ? (
                <>
                  <Loader className="w-4 h-4 animate-spin inline mr-2" />
                  Processing Actions...
                </>
              ) : (
                `Process Selected Actions (${selectedOptions.size})`
              )}
            </button>
          )}
          
          <button
            onClick={handleCompleteProcessing}
            className="w-full px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700"
          >
            <CheckCircle className="w-4 h-4 inline mr-2" />
            Mark as Fully Processed
          </button>
        </div>
      </div>

      {/* Modals - these will be implemented next */}
      {showDeadlineModal && (
        <ProcessingModal
          document={document}
          dbUserId={dbUserId}
          isDeadlineOnly={true}
          onClose={() => {
            setShowDeadlineModal(false);
            setSelectedOptions(new Set());
          }}
          onProcessed={() => {
            onUpdate();
            setShowDeadlineModal(false);
            setSelectedOptions(new Set());
          }}
        />
      )}

      {showDeliverableModal && (
        <TemplateModal
          isOpen={true}
          onClose={() => {
            setShowDeliverableModal(false);
            setSelectedOptions(new Set());
          }}
          onSuccess={async () => {
            await db.documentActions.create({
              document_id: document.id,
              user_id: dbUserId,
              action_type: 'create_deliverable',
              action_data: {}
            });
            
            setShowDeliverableModal(false);
            toast.success('Deliverable created successfully');
            onUpdate();
          }}
        />
      )}

      {showAddTaskModal && (
        <AddTaskModal
          document={document}
          dbUserId={dbUserId}
          onClose={() => {
            setShowAddTaskModal(false);
            setSelectedOptions(new Set());
          }}
          onSuccess={() => {
            setShowAddTaskModal(false);
            setSelectedOptions(new Set());
            onUpdate();
          }}
        />
      )}

      {showCompleteTaskModal && (
        <CompleteTaskModal
          document={document}
          dbUserId={dbUserId}
          onClose={() => {
            setShowCompleteTaskModal(false);
            setSelectedOptions(new Set());
          }}
          onSuccess={() => {
            setShowCompleteTaskModal(false);
            setSelectedOptions(new Set());
            onUpdate();
          }}
        />
      )}
    </>
  );
}